import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Lock, Settings as SettingsIcon, Phone, PhoneOff, RefreshCw, Plus, Trash2, Check, CreditCard, Globe, Calendar } from 'lucide-react';
import apiService from './services/api';

function SettingsPage() {
  const [activeTab, setActiveTab] = useState('account');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);

  // Check if current user is admin (full access to all settings)
  const isAdmin = user?.role === 'admin';

  // Fetch user profile on mount
  useEffect(() => {
    apiService.get('/auth/profile/')
      .then(userData => setUser(userData))
      .catch(() => {});
  }, []);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Phone forwarding state
  const [phoneOptions, setPhoneOptions] = useState([]);
  const [activeOptionId, setActiveOptionId] = useState(null);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [phoneSuccess, setPhoneSuccess] = useState('');

  // New option form
  const [newLabel, setNewLabel] = useState('');
  const [newNumber, setNewNumber] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // Blocked numbers state
  const [blockedNumbers, setBlockedNumbers] = useState([]);
  const [newBlockedNumber, setNewBlockedNumber] = useState('');
  const [blockedLoading, setBlockedLoading] = useState(false);
  const [blockedError, setBlockedError] = useState('');
  const [blockedSuccess, setBlockedSuccess] = useState('');

  // Stripe mode state
  const [stripeMode, setStripeMode] = useState('test');
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState('');
  const [stripeSuccess, setStripeSuccess] = useState('');

  // Excluded IPs state
  const [excludedIps, setExcludedIps] = useState([]);
  const [newIp, setNewIp] = useState('');
  const [ipsLoading, setIpsLoading] = useState(false);
  const [ipsError, setIpsError] = useState('');
  const [ipsSuccess, setIpsSuccess] = useState('');

  // Booking settings state
  const [minBookingDays, setMinBookingDays] = useState(3);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingSuccess, setBookingSuccess] = useState('');

  // Load settings on tab change
  useEffect(() => {
    if (activeTab === 'phone') {
      loadPhoneConfig();
      loadBlockedNumbers();
    }
    if (activeTab === 'payments') {
      loadStripeMode();
    }
    if (activeTab === 'traffic') {
      loadExcludedIps();
    }
    if (activeTab === 'booking') {
      loadBookingSettings();
    }
  }, [activeTab]);

  const loadStripeMode = async () => {
    setStripeLoading(true);
    setStripeError('');
    try {
      const response = await fetch(`${apiService.baseURL}/amn/settings/stripe_mode/`);
      if (response.ok) {
        const data = await response.json();
        setStripeMode(data.mode);
      }
    } catch (err) {
      setStripeError('Failed to load Stripe mode');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleStripeModeChange = async (newMode) => {
    setStripeLoading(true);
    setStripeError('');
    setStripeSuccess('');
    try {
      const response = await fetch(`${apiService.baseURL}/amn/settings/stripe_mode/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: newMode }),
      });
      if (response.ok) {
        setStripeMode(newMode);
        setStripeSuccess(`Switched to ${newMode} mode`);
      } else {
        const data = await response.json();
        setStripeError(data.error || 'Failed to update mode');
      }
    } catch (err) {
      setStripeError('Failed to connect to server');
    } finally {
      setStripeLoading(false);
    }
  };

  const loadExcludedIps = async () => {
    setIpsLoading(true);
    setIpsError('');
    try {
      const response = await fetch(`${apiService.baseURL}/amn/settings/excluded_ips/`);
      if (response.ok) {
        const data = await response.json();
        setExcludedIps(data.ips || []);
      } else {
        setIpsError('Failed to load excluded IPs');
      }
    } catch {
      setIpsError('Failed to connect to server');
    } finally {
      setIpsLoading(false);
    }
  };

  const handleAddIp = async (e) => {
    e.preventDefault();
    if (!newIp.trim()) return;
    setIpsLoading(true);
    setIpsError('');
    setIpsSuccess('');
    try {
      const response = await fetch(`${apiService.baseURL}/amn/settings/excluded_ips/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip: newIp.trim() }),
      });
      if (response.ok) {
        const data = await response.json();
        setExcludedIps(data.ips);
        setNewIp('');
        setIpsSuccess('IP added');
      } else {
        const data = await response.json();
        setIpsError(data.error || 'Failed to add IP');
      }
    } catch {
      setIpsError('Failed to connect to server');
    } finally {
      setIpsLoading(false);
    }
  };

  const handleRemoveIp = async (ip) => {
    setIpsLoading(true);
    setIpsError('');
    setIpsSuccess('');
    try {
      const response = await fetch(`${apiService.baseURL}/amn/settings/excluded_ips/`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip }),
      });
      if (response.ok) {
        const data = await response.json();
        setExcludedIps(data.ips);
        setIpsSuccess('IP removed');
      } else {
        setIpsError('Failed to remove IP');
      }
    } catch {
      setIpsError('Failed to connect to server');
    } finally {
      setIpsLoading(false);
    }
  };

  const loadBookingSettings = async () => {
    setBookingLoading(true);
    setBookingError('');
    try {
      const response = await fetch(`${apiService.baseURL}/amn/settings/min_booking_days/`);
      if (response.ok) {
        const data = await response.json();
        setMinBookingDays(data.min_days || 3);
      }
    } catch {
      setBookingError('Failed to load booking settings');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleSaveBookingSettings = async () => {
    setBookingLoading(true);
    setBookingError('');
    setBookingSuccess('');
    try {
      const response = await fetch(`${apiService.baseURL}/amn/settings/min_booking_days/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ min_days: minBookingDays }),
      });
      if (response.ok) {
        setBookingSuccess('Booking settings saved');
      } else {
        const data = await response.json();
        setBookingError(data.error || 'Failed to save settings');
      }
    } catch {
      setBookingError('Failed to connect to server');
    } finally {
      setBookingLoading(false);
    }
  };

  const loadPhoneConfig = async () => {
    setPhoneLoading(true);
    setPhoneError('');
    try {
      const response = await fetch(`${apiService.baseURL}/amn/phone_config`);
      if (response.ok) {
        const data = await response.json();
        setPhoneOptions(data.options || []);
        setActiveOptionId(data.active_id);
      } else {
        setPhoneError('Failed to load phone configuration');
      }
    } catch (err) {
      setPhoneError('Failed to connect to server');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleAddOption = async (e) => {
    e.preventDefault();
    if (!newLabel.trim() || !newNumber.trim()) {
      setPhoneError('Label and number are required');
      return;
    }

    setPhoneError('');
    setPhoneSuccess('');
    setPhoneLoading(true);

    try {
      const response = await fetch(`${apiService.baseURL}/amn/phone_config/numbers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'add',
          label: newLabel.trim(),
          number: newNumber.trim(),
        }),
      });

      if (response.ok) {
        setPhoneSuccess('Forwarding option added!');
        setNewLabel('');
        setNewNumber('');
        setShowAddForm(false);
        loadPhoneConfig();
      } else {
        const data = await response.json();
        setPhoneError(data.error || 'Failed to add option');
      }
    } catch (err) {
      setPhoneError('Failed to connect to server');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleDeleteOption = async (id) => {
    if (!window.confirm('Delete this forwarding option?')) return;

    setPhoneError('');
    setPhoneSuccess('');
    setPhoneLoading(true);

    try {
      const response = await fetch(`${apiService.baseURL}/amn/phone_config/numbers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', id }),
      });

      if (response.ok) {
        setPhoneSuccess('Option deleted');
        loadPhoneConfig();
      } else {
        const data = await response.json();
        setPhoneError(data.error || 'Failed to delete option');
      }
    } catch (err) {
      setPhoneError('Failed to connect to server');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleSetActive = async (id) => {
    setPhoneError('');
    setPhoneSuccess('');
    setPhoneLoading(true);

    try {
      const response = await fetch(`${apiService.baseURL}/amn/phone_config/numbers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'set_active', id }),
      });

      if (response.ok) {
        const data = await response.json();
        setPhoneSuccess(`Switched to: ${data.active_option?.label}`);
        loadPhoneConfig();
      } else {
        const data = await response.json();
        setPhoneError(data.error || 'Failed to switch');
      }
    } catch (err) {
      setPhoneError('Failed to connect to server');
    } finally {
      setPhoneLoading(false);
    }
  };

  const loadBlockedNumbers = async () => {
    setBlockedLoading(true);
    setBlockedError('');
    try {
      const data = await apiService.get('/amn/phone_config/blocked/');
      setBlockedNumbers(data.blocked_numbers || []);
    } catch {
      setBlockedError('Failed to load blocked numbers');
    } finally {
      setBlockedLoading(false);
    }
  };

  const handleBlockNumber = async (e) => {
    e.preventDefault();
    if (!newBlockedNumber.trim()) return;
    setBlockedLoading(true);
    setBlockedError('');
    setBlockedSuccess('');
    try {
      const data = await apiService.post('/amn/phone_config/blocked/', { phone: newBlockedNumber.trim() });
      setBlockedNumbers(data.blocked_numbers || []);
      setNewBlockedNumber('');
      setBlockedSuccess('Number blocked');
    } catch (err) {
      setBlockedError(err?.response?.data?.error || 'Failed to block number');
    } finally {
      setBlockedLoading(false);
    }
  };

  const handleUnblockNumber = async (phone) => {
    setBlockedLoading(true);
    setBlockedError('');
    setBlockedSuccess('');
    try {
      const data = await apiService.post('/amn/phone_config/blocked/', { phone, action: 'unblock' });
      setBlockedNumbers(data.blocked_numbers || []);
      setBlockedSuccess('Number unblocked');
    } catch {
      setBlockedError('Failed to unblock number');
    } finally {
      setBlockedLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long.');
      return;
    }

    setLoading(true);

    try {
      await apiService.changePassword(currentPassword, newPassword);
      setSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-neutral-50 p-6" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="max-w-4xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-2">
            <SettingsIcon className="w-6 h-6 text-red-800" />
            <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
          </div>
          <p className="text-sm text-gray-600">Manage your account settings and preferences</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b border-gray-200">
            <button
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'account'
                  ? 'text-red-800 border-b-2 border-red-800'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              onClick={() => setActiveTab('account')}
            >
              <div className="flex items-center space-x-2">
                <Lock className="w-4 h-4" />
                <span>Account Security</span>
              </div>
            </button>
            <button
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'phone'
                  ? 'text-red-800 border-b-2 border-red-800'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              onClick={() => setActiveTab('phone')}
            >
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>Phone Forwarding</span>
              </div>
            </button>
            {isAdmin && (
              <button
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'payments'
                    ? 'text-red-800 border-b-2 border-red-800'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setActiveTab('payments')}
              >
                <div className="flex items-center space-x-2">
                  <CreditCard className="w-4 h-4" />
                  <span>Payments</span>
                </div>
              </button>
            )}
            {isAdmin && (
              <button
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'traffic'
                    ? 'text-red-800 border-b-2 border-red-800'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setActiveTab('traffic')}
              >
                <div className="flex items-center space-x-2">
                  <Globe className="w-4 h-4" />
                  <span>Website Traffic</span>
                </div>
              </button>
            )}
            <button
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'booking'
                  ? 'text-red-800 border-b-2 border-red-800'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
              onClick={() => setActiveTab('booking')}
            >
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>Booking</span>
              </div>
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'account' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Change Password</h2>

                {success && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-green-800">{success}</span>
                  </div>
                )}

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-red-800">{error}</span>
                  </div>
                )}

                <form onSubmit={handlePasswordChange} className="max-w-md">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-800"
                      disabled={loading}
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-800"
                      disabled={loading}
                      required
                      minLength={8}
                    />
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-800"
                      disabled={loading}
                      required
                      minLength={8}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center space-x-2 px-6 py-2.5 rounded font-medium bg-red-800 hover:bg-red-900 text-white disabled:bg-gray-400"
                  >
                    <Lock className="w-4 h-4" />
                    <span>{loading ? 'Changing...' : 'Change Password'}</span>
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'phone' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800">Phone Forwarding</h2>
                  <button
                    onClick={loadPhoneConfig}
                    disabled={phoneLoading}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <RefreshCw className={`w-4 h-4 ${phoneLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                {phoneSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-sm text-green-800">{phoneSuccess}</span>
                  </div>
                )}

                {phoneError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                    <span className="text-sm text-red-800">{phoneError}</span>
                  </div>
                )}

                <p className="text-sm text-gray-600 mb-4">
                  Configure phone numbers for call forwarding. Click on an option to make it active.
                </p>

                {/* Options List */}
                <div className="space-y-2 mb-4">
                  {phoneOptions.length === 0 ? (
                    <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg text-center">
                      No forwarding options configured. Add one below.
                    </div>
                  ) : (
                    phoneOptions.map((option) => (
                      <div
                        key={option.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          option.id === activeOptionId
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <button
                          onClick={() => handleSetActive(option.id)}
                          className="flex-1 flex items-center space-x-3 text-left"
                          disabled={phoneLoading}
                        >
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            option.id === activeOptionId
                              ? 'border-green-500 bg-green-500'
                              : 'border-gray-300'
                          }`}>
                            {option.id === activeOptionId && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-gray-800">{option.label}</div>
                            <div className="text-sm text-gray-500">{option.number}</div>
                          </div>
                        </button>
                        <button
                          onClick={() => handleDeleteOption(option.id)}
                          disabled={phoneLoading}
                          className="p-2 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* Add New Option */}
                {showAddForm ? (
                  <form onSubmit={handleAddOption} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Add Forwarding Option</h3>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Label</label>
                        <input
                          type="text"
                          value={newLabel}
                          onChange={(e) => setNewLabel(e.target.value)}
                          placeholder="e.g., Jade, Office, Backup"
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-800"
                          disabled={phoneLoading}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Phone Number</label>
                        <input
                          type="tel"
                          value={newNumber}
                          onChange={(e) => setNewNumber(e.target.value)}
                          placeholder="+1XXXXXXXXXX"
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-800"
                          disabled={phoneLoading}
                        />
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="submit"
                        disabled={phoneLoading}
                        className="px-4 py-2 bg-red-800 text-white rounded text-sm font-medium hover:bg-red-900 disabled:bg-gray-400"
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowAddForm(false); setNewLabel(''); setNewNumber(''); }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-red-800 hover:bg-red-50 rounded"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Forwarding Option</span>
                  </button>
                )}

                {/* Blocked Numbers Section */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <PhoneOff className="w-5 h-5 text-red-600" />
                    <h3 className="text-lg font-semibold text-gray-800">Blocked Numbers</h3>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    Calls from these numbers will be automatically rejected and won't be forwarded.
                  </p>

                  {blockedSuccess && (
                    <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                      <span className="text-sm text-green-800">{blockedSuccess}</span>
                    </div>
                  )}

                  {blockedError && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded flex items-center">
                      <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                      <span className="text-sm text-red-800">{blockedError}</span>
                    </div>
                  )}

                  {blockedNumbers.length > 0 ? (
                    <div className="space-y-2 mb-4">
                      {blockedNumbers.map((num) => (
                        <div key={num} className="flex items-center justify-between p-3 bg-red-50 rounded border border-red-200">
                          <div className="flex items-center space-x-2">
                            <PhoneOff className="w-4 h-4 text-red-400" />
                            <span className="text-sm font-mono text-gray-800">{num.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}</span>
                          </div>
                          <button
                            onClick={() => handleUnblockNumber(num)}
                            disabled={blockedLoading}
                            className="text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                            title="Unblock this number"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 mb-4">No numbers blocked yet.</p>
                  )}

                  <form onSubmit={handleBlockNumber} className="flex items-center space-x-2">
                    <input
                      type="tel"
                      value={newBlockedNumber}
                      onChange={(e) => setNewBlockedNumber(e.target.value)}
                      placeholder="Phone number to block"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-800 text-sm"
                      disabled={blockedLoading}
                    />
                    <button
                      type="submit"
                      disabled={blockedLoading || !newBlockedNumber.trim()}
                      className="px-4 py-2 bg-red-800 text-white rounded hover:bg-red-900 transition-colors disabled:opacity-50 flex items-center space-x-1"
                    >
                      <PhoneOff className="w-4 h-4" />
                      <span className="text-sm">Block</span>
                    </button>
                  </form>
                </div>
              </div>
            )}

            {activeTab === 'payments' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Payment Settings</h2>

                {stripeSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-sm text-green-800">{stripeSuccess}</span>
                  </div>
                )}

                {stripeError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                    <span className="text-sm text-red-800">{stripeError}</span>
                  </div>
                )}

                <div className="max-w-md">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stripe Mode</label>
                  <p className="text-sm text-gray-500 mb-4">
                    Switch between test and live mode for gift card payments.
                  </p>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleStripeModeChange('test')}
                      disabled={stripeLoading}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors ${
                        stripeMode === 'test'
                          ? 'border-yellow-500 bg-yellow-50 text-yellow-800'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        {stripeMode === 'test' && <Check className="w-4 h-4" />}
                        <span>Test Mode</span>
                      </div>
                      <div className="text-xs mt-1 opacity-75">For testing payments</div>
                    </button>

                    <button
                      onClick={() => handleStripeModeChange('live')}
                      disabled={stripeLoading}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors ${
                        stripeMode === 'live'
                          ? 'border-green-500 bg-green-50 text-green-800'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        {stripeMode === 'live' && <Check className="w-4 h-4" />}
                        <span>Live Mode</span>
                      </div>
                      <div className="text-xs mt-1 opacity-75">Real payments</div>
                    </button>
                  </div>

                  {stripeMode === 'test' && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                      <strong>Test Mode Active:</strong> Use Stripe test cards (e.g., 4242 4242 4242 4242)
                    </div>
                  )}

                  {stripeMode === 'live' && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                      <strong>Live Mode Active:</strong> Real payments will be processed
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'traffic' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Website Traffic Settings</h2>

                {ipsSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-green-800">{ipsSuccess}</span>
                  </div>
                )}

                {ipsError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-red-800">{ipsError}</span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Excluded IP Addresses</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Traffic from these IPs will not be counted in website metrics or conversion tracking.
                  </p>

                  {excludedIps.length > 0 ? (
                    <div className="space-y-2 mb-4">
                      {excludedIps.map((ip) => (
                        <div key={ip} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
                          <span className="text-sm font-mono text-gray-800">{ip}</span>
                          <button
                            onClick={() => handleRemoveIp(ip)}
                            disabled={ipsLoading}
                            className="text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 mb-4">No IPs excluded yet.</p>
                  )}

                  <form onSubmit={handleAddIp} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={newIp}
                      onChange={(e) => setNewIp(e.target.value)}
                      placeholder="e.g. 192.168.1.1"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-800 text-sm font-mono"
                      disabled={ipsLoading}
                    />
                    <button
                      type="submit"
                      disabled={ipsLoading || !newIp.trim()}
                      className="px-4 py-2 bg-red-800 text-white rounded hover:bg-red-900 transition-colors disabled:opacity-50 flex items-center space-x-1"
                    >
                      <Plus className="w-4 h-4" />
                      <span className="text-sm">Add</span>
                    </button>
                  </form>
                </div>
              </div>
            )}

            {activeTab === 'booking' && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Online Booking Settings</h2>

                {bookingSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-green-800">{bookingSuccess}</span>
                  </div>
                )}

                {bookingError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-red-800">{bookingError}</span>
                  </div>
                )}

                <div className="max-w-md">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Days in Advance
                  </label>
                  <p className="text-sm text-gray-500 mb-4">
                    Customers must book at least this many days ahead. This prevents last-minute bookings.
                  </p>

                  <div className="flex items-center space-x-4 mb-6">
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={minBookingDays}
                      onChange={(e) => setMinBookingDays(parseInt(e.target.value) || 1)}
                      className="w-24 px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-800 text-center text-lg"
                      disabled={bookingLoading}
                    />
                    <span className="text-gray-600">days</span>
                  </div>

                  <div className="p-3 bg-gray-50 border border-gray-200 rounded mb-6">
                    <p className="text-sm text-gray-600">
                      <strong>Current setting:</strong> Customers can book appointments starting{' '}
                      <span className="font-semibold text-red-800">{minBookingDays} day{minBookingDays !== 1 ? 's' : ''}</span>{' '}
                      from today.
                    </p>
                  </div>

                  <button
                    onClick={handleSaveBookingSettings}
                    disabled={bookingLoading}
                    className="flex items-center space-x-2 px-6 py-2.5 rounded font-medium bg-red-800 hover:bg-red-900 text-white disabled:bg-gray-400"
                  >
                    <Check className="w-4 h-4" />
                    <span>{bookingLoading ? 'Saving...' : 'Save Settings'}</span>
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
